import { setUser } from '@sentry/react';
import { useAuth } from 'lib/auth';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { type SessionUser } from 'types/auth';

/** The person profile every event is attributed to. */
const personProperties = (user: SessionUser) => ({
  email: user.email,
  kapa_email: user.email,
  kapa_user_id: user.id,
  kapa_role: user.role,
  kapa_team_name: user.team?.name,
  kapa_team_id: user.team?.id,
  kapa_team_tier: user.team?.tier,
  kapa_trial_ends_at: user.team?.trial_ends_at,
  kapa_is_staff_viewing_external_team: user.is_staff_viewing_external_team,
});

/** Group properties, so team-level insights can break down by plan. */
const groupProperties = (team: NonNullable<SessionUser['team']>) => ({
  name: team.name,
  tier: team.tier,
});

// Mounted outside the router, so it runs for a teamless account and reads `session`.
const SetUsers = () => {
  const auth = useAuth();
  const sessionUser = auth.session?.user;
  // The distinct id last sent. PostHog ignores a repeat `identify` for the same
  // person, so only a different one re-identifies; property changes go through
  // `setPersonProperties` below. A first load must not reset.
  const identifiedAs = useRef<string | null>(null);
  // Serialized so a property change refreshes the profile even when the person
  // is unchanged, which a dedupe key over a subset of them would miss.
  const sentProperties = useRef<string | null>(null);
  const sentGroup = useRef<string | null>(null);

  useEffect(() => {
    if (sessionUser?.email) {
      const properties = personProperties(sessionUser);
      const serialized = JSON.stringify(properties);

      if (identifiedAs.current !== sessionUser.email) {
        identifiedAs.current = sessionUser.email;
        sentProperties.current = serialized;
        setUser({ email: sessionUser.email });
        posthog.identify(sessionUser.email, properties);
      } else if (sentProperties.current !== serialized) {
        sentProperties.current = serialized;
        posthog.setPersonProperties(properties);
      }

      const team = sessionUser.team;
      if (team) {
        const group = JSON.stringify([team.id, groupProperties(team)]);
        if (sentGroup.current !== group) {
          sentGroup.current = group;
          posthog.group('team', team.id, groupProperties(team));
        }
      }
      return;
    }

    // Clear only once a signed-in user goes away; resetting before anyone was
    // identified would churn the anonymous id on every page load.
    if (identifiedAs.current !== null) {
      identifiedAs.current = null;
      sentProperties.current = null;
      sentGroup.current = null;
      setUser(null);
      posthog.reset();
    }
  }, [sessionUser]);

  return null;
};

export default SetUsers;
